// Gantt/scheduling computation pipeline for FlowchartBuilder, extracted out of
// the component body. Pure re-wiring around already-unit-tested lib/gantt.ts
// functions (see lib/gantt.test.ts) — no scheduling logic lives here, everything
// calls straight through exactly as before. See CLAUDE.md: is24h/scheduling
// logic belongs only in lib/gantt.ts, never re-derived elsewhere.
//
// Every useMemo dependency array here — including the eslint-disable overrides —
// was hand-tuned this session against a real performance regression on a
// 113-box/155-arrow flowchart (recomputing the full schedule/critical-path pass
// on every drag pixel or keystroke). Do not "clean up" these dependency arrays;
// doing so risks silently reintroducing that lag.
import { useMemo } from 'react';
import {
  calcSchedule,
  calcCriticalPath,
  computeVisualPositions,
  calcSchedulingConflicts,
  calcEarliestStartOffset as ganttCalcEarliestStartOffset,
  addWeekdayHours,
  applySubitemRollup,
  calcSubitemGanttTasks,
  walkIs24hRealStart,
  firstValidDay0,
} from './lib/gantt';
import type { Box, Arrow, DurationUnit } from './types/index';
import type { EndTimeDisplay } from './useUserFlowchartViewSettings';

const DISPLAY_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DISPLAY_DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

// Shared by getStart/getEnd below (used to be duplicated inline in each as
// separate withDay/withYear/dateOnly checks against the old 5-string enum —
// collapsed into one function once endTimeDisplay became 4 independent
// booleans instead). Month is dropped whenever day+date+time are ALL set,
// regardless of year, to keep the string from getting too long.
function formatDisplayDate(d: Date, flags: EndTimeDisplay): string {
  const showMonth = !(flags.day && flags.date && flags.time);
  const parts: string[] = [];
  if (flags.day) parts.push(DISPLAY_DAYS[d.getDay()]);
  if (flags.date) parts.push(showMonth ? `${d.getDate()} ${DISPLAY_MONTHS[d.getMonth()]}` : `${d.getDate()}`);
  if (flags.year) parts.push(`${d.getFullYear()}`);
  let str = parts.join(' ');
  if (flags.time) {
    // When the month is dropped (showMonth false), "15 14:00" reads as one run-on
    // number — a comma before the time gives the date a clearer visual edge. With
    // the month present ("15 Jan 14:00") that ambiguity doesn't exist, so it stays
    // a plain space there.
    const sep = str ? (showMonth ? ' ' : ', ') : '';
    str += `${sep}${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }
  return str;
}

interface UseGanttScheduleParams {
  boxes: Box[];
  arrows: Arrow[];
  durationUnit: DurationUnit;
  fullDaySchedule: boolean;
  workHoursOnly: boolean;
  ganttWorkDays: number[];
  ganttNonWorkingDays: string[];
  ganttStartDate: Date;
  workDayStartHour: number;
  workDayHoursPerDay: number;
  endTimeDisplay: EndTimeDisplay;
  showCriticalPathCanvas: boolean;
}

export function useGanttSchedule(params: UseGanttScheduleParams) {
  const {
    boxes, arrows, durationUnit, fullDaySchedule, workHoursOnly, ganttWorkDays,
    ganttNonWorkingDays, ganttStartDate, workDayStartHour, workDayHoursPerDay,
    endTimeDisplay, showCriticalPathCanvas,
  } = params;

  // Thin wrapper — binds component state to the pure exported helper.
  // Still needed for getAdjStart and the earliest-start indicator in the Gantt render block.
  const calcEarliestStartOffset = (box: { id: number; earliestStart?: string }): number =>
    ganttCalcEarliestStartOffset(box, ganttStartDate, durationUnit, fullDaySchedule, workHoursOnly, ganttNonWorkingDays, ganttWorkDays, workDayStartHour, workDayHoursPerDay);

  // Maps Arrow (from/to) → ScheduleLink (fromBox/toBox) once; shared by all three memos below.
  const ganttLinks = useMemo(
    () => arrows.map(a => ({ id: a.id, fromBox: a.fromBox ?? a.from, toBox: a.toBox ?? a.to })),
    [arrows],
  );

  // Scheduling/Gantt math (and everything memoized below that depends on it) only
  // cares about a box's non-visual fields (duration, dependencies-via-arrows, pins,
  // is24h flags, resourceHours, etc) — never x/y/width/height. Dragging or resizing a
  // box changes only those visual fields, but changes `boxes`' array reference on
  // every mousemove; without this, calcSchedule/computeVisualPositions/
  // calcCriticalPath/calcSchedulingConflicts (each with its own O(n) or worse passes
  // over all boxes) were re-running on every single pixel of every drag, which is
  // what made large flowcharts (100+ boxes) feel sluggish even solo. This fingerprint
  // is cheap to compute (a JSON.stringify, not a scheduling pass) and only changes
  // reference when something scheduling-relevant actually changed, so the expensive
  // memos below skip recomputation during a pure move/resize.
  //
  // text/title is excluded for the same reason, with one exception: a 'startday'
  // box's text IS its schedule offset (calcSchedule/computeVisualPositions in
  // lib/gantt.ts parseInt() a startday predecessor's text as its day number), so it
  // has to stay in the fingerprint for that type. For every other type, text/title
  // is pure display content that scheduling never reads — leaving it in meant typing
  // into a note (or any box title) re-ran the full schedule/critical-path/conflicts
  // pass on every keystroke, which was very noticeable on a large flowchart.
  const schedulingFingerprint = useMemo(
    () => JSON.stringify(boxes.map(({ x, y, width, height, text, title, ...rest }) =>
      rest.type === 'startday' ? { ...rest, text, title } : rest
    )),
    [boxes],
  );

  // Subitems (see lib/gantt.ts's own comment on applySubitemRollup/Box.parentBoxId) —
  // a no-op today since nothing sets parentBoxId yet. applySubitemRollup gives any box
  // with subitems a rolled-up duration; outerBoxes/outerLinks then exclude subitems
  // entirely from here on, so every downstream consumer (ganttTasks, arrowBoxes,
  // renderedArrows, the Gantt panel's own task list, ...) already only ever sees
  // top-level boxes, instead of each one needing its own parentBoxId filter. Subitems
  // are edited via their own popup, never shown on the main canvas or Gantt panel.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const { outerBoxes, outerLinks, topLevelArrows } = useMemo(() => {
    // Explicit generic instantiation so outerBoxes' TS type carries rolledUpDuration
    // (Box itself, correctly, doesn't declare it — it's a computed scheduling value,
    // never persisted) — needed so getEnd/getEndDate's outerBoxesById lookup below
    // can read it.
    const rolledUp = applySubitemRollup<Box & { rolledUpDuration?: number }>(boxes, ganttLinks, {
      durationUnit, fullDaySchedule, workHoursOnly, workDays: ganttWorkDays,
      nonWorkingDays: ganttNonWorkingDays, startDate: ganttStartDate,
      dayStartHour: workDayStartHour, hoursPerDay: workDayHoursPerDay,
    });
    const outerBoxes = rolledUp.filter(b => b.parentBoxId === undefined);
    const topLevelIds = new Set(outerBoxes.map(b => b.id));
    const outerLinks = ganttLinks.filter(l => topLevelIds.has(l.fromBox) && topLevelIds.has(l.toBox));
    // ganttLinks is index-aligned with arrows (mapped 1:1, same ids) — reuse the filter
    // result by id instead of re-resolving fromBox/toBox's from/to legacy-alias fallback.
    const outerLinkIds = new Set(outerLinks.map(l => l.id));
    const topLevelArrows = arrows.filter(a => outerLinkIds.has(a.id));
    return { outerBoxes, outerLinks, topLevelArrows };
  }, [schedulingFingerprint, ganttLinks, arrows, durationUnit, fullDaySchedule, workHoursOnly, ganttNonWorkingDays, ganttStartDate, workDayStartHour, workDayHoursPerDay]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lookup for getEnd/getEndDate's rolledUpDuration guard — built from outerBoxes
  // (which actually carries rolledUpDuration), not boxById (built from raw boxes,
  // which never does).
  const outerBoxesById = useMemo(() => new Map(outerBoxes.map(b => [b.id, b])), [outerBoxes]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const ganttTasks = useMemo(
    () => calcSchedule(
      outerBoxes,
      outerLinks,
      { durationUnit, fullDaySchedule, workHoursOnly, workDays: ganttWorkDays, nonWorkingDays: ganttNonWorkingDays, startDate: ganttStartDate, dayStartHour: workDayStartHour, hoursPerDay: workDayHoursPerDay },
    ),
    [outerBoxes, outerLinks, durationUnit, fullDaySchedule, workHoursOnly, ganttNonWorkingDays, ganttStartDate, workDayStartHour, workDayHoursPerDay], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Each subitem's own scheduled position, shifted to its parent's actual absolute
  // start — for GanttPanel to optionally render as indented rows directly under the
  // parent's row (a separate concern from rolledUpDuration/outerBoxes above, which
  // collapse subitems into their parent's single bar; this instead exposes them
  // individually). Gated on ganttTasks + schedulingFingerprint, not raw `boxes`, for
  // the same reason as outerBoxes above — recomputing this per drag pixel would
  // reintroduce the exact perf regression schedulingFingerprint exists to prevent.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const subitemGanttTasks = useMemo(() => {
    const result: typeof ganttTasks = [];
    for (const task of ganttTasks) {
      result.push(...calcSubitemGanttTasks(task.id, task.start, boxes, ganttLinks, {
        durationUnit, fullDaySchedule, workHoursOnly, workDays: ganttWorkDays,
        nonWorkingDays: ganttNonWorkingDays, startDate: ganttStartDate,
        dayStartHour: workDayStartHour, hoursPerDay: workDayHoursPerDay,
      }));
    }
    return result;
  }, [ganttTasks, schedulingFingerprint, ganttLinks, durationUnit, fullDaySchedule, workHoursOnly, ganttNonWorkingDays, ganttStartDate, workDayStartHour, workDayHoursPerDay]); // eslint-disable-line react-hooks/exhaustive-deps

  const ganttVisualPositions = useMemo(
    () => computeVisualPositions(ganttTasks, outerLinks, outerBoxes, {
      durationUnit,
      fullDaySchedule,
      workDays: ganttWorkDays,
      nonWorkingDays: ganttNonWorkingDays,
      startDate: ganttStartDate ?? new Date(),
      dayStartHour: workDayStartHour,
      hoursPerDay: workDayHoursPerDay,
    }),
    [ganttTasks, outerLinks, outerBoxes, durationUnit, fullDaySchedule, ganttWorkDays, ganttNonWorkingDays, ganttStartDate, workDayStartHour, workDayHoursPerDay], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // outerLinks, not ganttLinks — ganttTasks only ever contains top-level boxes (see
  // outerBoxes above), and subitem-internal arrows (ganttLinks also carries those)
  // reference ids that can never appear as a cp/conflict task here. Harmless today
  // (confirmed: subitem arrows only ever connect subitem ids of the same parent, so
  // they form a component unreachable from any top-level task), but outerLinks is
  // already the correct, already-filtered list every other consumer in this hook
  // uses — no reason for these two to be the one exception.
  const criticalPath = useMemo(
    () => calcCriticalPath(ganttTasks, outerLinks, ganttVisualPositions),
    [ganttTasks, outerLinks, ganttVisualPositions],
  );

  const schedulingConflicts = useMemo(
    () => calcSchedulingConflicts(ganttTasks, outerLinks, ganttVisualPositions),
    [ganttTasks, outerLinks, ganttVisualPositions],
  );

  // getEnd/getStart (below) used to do a ganttTasks.find()/boxes.find() per box they
  // were called for, and they're called once per box while rendering the canvas —
  // O(n²) over the whole flowchart on every render, noticeable once there are 100+
  // boxes. Building these lookup maps costs one O(n) pass; a .get() per box then
  // makes the whole thing O(n) again. boxById is deliberately keyed to `boxes`
  // directly (not a fingerprint) — rebuilding it every render is cheap, and it needs
  // to reflect every box edit anyway, unlike the geometry-only fingerprints above.
  const ganttTaskById = useMemo(() => new Map(ganttTasks.map(t => [t.id, t])), [ganttTasks]);
  const boxById = useMemo(() => new Map(boxes.map(b => [b.id, b])), [boxes]);
  // getStartDate/getEndDate (below) look tasks up here too, unlike ganttTaskById above —
  // those stay top-level-only on purpose (canvas/Gantt labels never show for a subitem
  // id), but the XLSX export now needs real calendar dates for subitem rows as well.
  const ganttAndSubitemTaskById = useMemo(
    () => new Map([...ganttTasks, ...subitemGanttTasks].map(t => [t.id, t])),
    [ganttTasks, subitemGanttTasks],
  );

  // CanvasArrow only reads a box's position/size (for connection points) and its
  // type (for callout detection) — see arrowRouting.ts. It's passed the whole
  // `boxes` array as a prop though, so editing anything else (note text, duration,
  // assignee, status, ...) still changed that prop's reference every keystroke,
  // defeating CanvasArrow's React.memo and re-running its waypoint/crossing-
  // detection math (up to O(arrows²)) for every single arrow on every keystroke —
  // same class of bug as schedulingFingerprint above, just on the rendering side.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const arrowGeometryFingerprint = useMemo(
    () => boxes.map(b => `${b.id}:${b.x}:${b.y}:${b.width}:${b.height}:${b.type}`).join('|'),
    [boxes],
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const arrowBoxes = useMemo(() => boxes, [arrowGeometryFingerprint]);
  // Id→box lookup built from arrowBoxes (not the plain boxById below, which is keyed to
  // raw `boxes` and so changes reference on every non-geometry edit too, e.g. typing a
  // note) — CanvasArrow only reads position/size/type, so this must share arrowBoxes'
  // geometry-only invalidation or every arrow would rebuild its lookup (and lose its
  // React.memo bail-out) on every keystroke, not just every drag frame.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const arrowBoxesById = useMemo(() => new Map(arrowBoxes.map(b => [b.id, b])), [arrowGeometryFingerprint]);

  // SVG has no z-index — render order determines stacking order. Without this
  // sort, a non-critical arrow can be drawn on top of the red critical arrow
  // for a box with multiple inbound arrows (stable sort: relative order
  // within each group stays unchanged, critical arrows are simply moved to the end).
  // Memoized so this array keeps a stable reference across renders where nothing
  // here actually changed (e.g. every mousemove while dragging a box) — CanvasArrow
  // is React.memo'd and does real work (waypoint/crossing computation) per arrow, so
  // a fresh [...arrows].sort() every frame defeated that memo for every single arrow
  // on every frame, which is very noticeable once a flowchart has 100+ arrows. Must
  // live before the showProjectGantt/showGantt early returns in FlowchartBuilder.tsx
  // (Rules of Hooks) — this hook's own call site there must stay before those.
  // Based on topLevelArrows (arrows already excluding subitem-internal dependency
  // arrows), not raw `arrows` — subitem arrows connect subitem boxes, which never
  // render on the main canvas, so they must never render as arrows there either.
  const renderedArrows = useMemo(() => (
    showCriticalPathCanvas
      ? [...topLevelArrows].sort((a, b) =>
          (criticalPath.arrowIds.has(a.id) ? 1 : 0) - (criticalPath.arrowIds.has(b.id) ? 1 : 0))
      : topLevelArrows
  ), [topLevelArrows, showCriticalPathCanvas, criticalPath]);

  /**
   * Gets the end time for a specific box
   * @param id - Box ID
   * @returns End time as string
   */
  // asPosition=true  → "what time is gantt position X?" (e.g. position 32 = June 26 09:00)
  // asPosition=false → "end of X hours of work"        (e.g. 8 work hours = June 22 17:00)
  const endOffsetToDate = (end: number, asPosition = false): Date => {
    const nwSet = new Set(ganttNonWorkingDays);
    // Schedule column 0 is defined as the first REAL work day — not raw
    // ganttStartDate itself, which can land on a weekend/non-working day (see
    // firstValidDay0's own doc comment in lib/gantt.ts). This function used to
    // anchor at raw ganttStartDate directly — a second, independently-written
    // copy of exactly the bug the original firstValidDay0 fix addressed in
    // lib/gantt.ts's own colToRealDate/computeVisualPositions, just never
    // ported here. Confirmed live: a Sunday gantt_start_date desynced this
    // decode from calcSchedule's own column arithmetic by a full work day,
    // showing a regular successor starting days before its is24h predecessor's
    // own (correctly computed) end.
    const d = firstValidDay0(ganttStartDate, workHoursOnly, ganttNonWorkingDays, ganttWorkDays);

    // A pinned (scheduledStart) task can land before ganttStartDate — calcStart
    // (lib/gantt.ts) and workDaysBetween both explicitly support a negative offset
    // for that case — so this must be able to walk backward too. `while (added < n)`
    // alone never even enters its body when n is negative (added starts at 0), which
    // silently left `d` at ganttStartDate itself: every date/time computed from a
    // negative offset collapsed to the same (wrong) value, which is exactly why an
    // end time could come out identical to its own start time instead of reflecting
    // the real duration.
    const addWorkDays = (n: number) => {
      const dir = n < 0 ? -1 : 1;
      let added = 0;
      while (added < Math.abs(n)) {
        d.setDate(d.getDate() + dir);
        const dow = d.getDay();
        if (!workHoursOnly || (ganttWorkDays.includes(dow) && !nwSet.has(d.toISOString().slice(0, 10)))) added++;
      }
    };

    const hpd = fullDaySchedule ? 24 : workDayHoursPerDay;
    const dayStart = fullDaySchedule ? 0 : workDayStartHour;
    if (durationUnit === 'hours') {
      const totalMin = Math.round(end * 60);
      const hpdMin = hpd * 60;
      let days = Math.floor(totalMin / hpdMin);
      let remMin = totalMin % hpdMin;
      // In end-time mode: "8 work hours done" = 17:00 same day, not 09:00 next day.
      // days > 0 used to gate this, which only ever fixed a task scheduled AT OR
      // AFTER ganttStartDate — once ganttStartDate itself floats to "today" (see
      // CLAUDE.md's gantt_start_date note) on a flowchart without one set, ANY
      // scheduledStart-pinned task before today gets a NEGATIVE schedule-column
      // offset, and a nonzero-duration task's own end can legitimately be an exact
      // multiple of the workday length with days < 0 too — the exact same
      // same-day-vs-next-day ambiguity this guard exists for, just on the other
      // side of the origin. Confirmed live: a 9h task pinned to start at 8:00 on a
      // 9h workday (finishing exactly at 17:00 the same day) decoded its finish as
      // 8:00 the FOLLOWING workday once ganttStartDate had floated past it.
      if (!asPosition && remMin === 0 && days !== 0) { days--; remMin = hpdMin; }
      addWorkDays(days);
      d.setHours(dayStart + Math.floor(remMin / 60), remMin % 60, 0, 0);
    } else if (durationUnit === 'weeks') {
      // Use exact minute arithmetic (same as hours branch) to avoid Math.round(7.5)→8 rounding shift
      const totalMinW = Math.round(end * 5 * hpd * 60);
      const hpdMinW = hpd * 60;
      let daysW = Math.floor(totalMinW / hpdMinW);
      let remMinW = totalMinW % hpdMinW;
      // Same days !== 0 fix as the hours branch above — see its own comment.
      if (!asPosition && remMinW === 0 && daysW !== 0) { daysW--; remMinW = hpdMinW; }
      addWorkDays(daysW);
      d.setHours(dayStart + Math.floor(remMinW / 60), remMinW % 60, 0, 0);
    } else {
      addWorkDays(Math.round(end));
      d.setHours(fullDaySchedule ? 0 : workDayStartHour + workDayHoursPerDay, 0, 0, 0);
    }
    return d;
  };

  // For alwaysOn tasks in hours mode, work-hour column positions don't reflect real clock time.
  // E.g. col 9 = work-hour "Fri 10:00" but may actually be "Thu 18:00" clock-wise for a chained task.
  // Thin wrapper around lib/gantt.ts's shared walkIs24hRealStart — this used to be its own,
  // hand-rolled reimplementation of that exact concept (contrary to CLAUDE.md's standing
  // warning against re-deriving is24h logic outside lib/gantt.ts), and two of its three
  // independently-found bugs were specifically divergences from what the shared skeleton
  // already got right: an own-task fallback that used end-of-work-day semantics for what
  // is really a start query, and an earliestStart check that only ever saw the floor's
  // influence already-lossily encoded into a work-day-only schedule column instead of as
  // a real Date (see walkIs24hRealStart's own doc comment, and computeVisualPositions'
  // applyEarliestStart below, which already did this correctly for its own identical need).
  const getAlwaysOnClockStart = (
    id: number,
    tasks: Array<Omit<Box, 'duration'> & { start: number; duration: number }>,
    fallbackOverride?: Date,
  ): Date => {
    const cache: Record<number, Date> = {};
    const visiting = new Set<number>();
    const taskById2 = new Map(tasks.map(tk => [tk.id, tk]));
    const calloutIds2 = new Set(boxes.filter(b => b.type === 'callout').map(b => b.id));
    // Matches calcSchedule's own fallback hour for a genuinely root (no predecessor, no
    // earliestStart) is24h task — see getEnd/getStart's identical endOffsetToDate(0, true)
    // decode for a non-is24h task at schedule position 0, which lands on the same instant.
    // A caller walking a SUBITEM instead passes fallbackOverride = its parent's own real
    // clock start (see is24hRealStarts below) — a root subitem (no predecessor within its
    // own group) begins exactly when its parent does, per calcSubitemGanttTasks's shift.
    const fallback = fallbackOverride ?? (() => {
      const d = new Date(ganttStartDate);
      d.setHours(workDayStartHour, 0, 0, 0);
      return d;
    })();

    const walk = (bid: number): Date => walkIs24hRealStart(bid, cache, visiting, {
      getScheduledStart: (x) => boxes.find(b => b.id === x)?.scheduledStart,
      // Matches calcStart's own is24h scheduledStart-pin encoding (dayStartH=0 for an
      // is24h box) — a date-only pin means midnight, not the configured workday start.
      scheduledStartDefaultHour: 0,
      fallback,
      getPredecessorIds: (x) => arrows.filter(a => (a.toBox ?? a.to) === x && !calloutIds2.has(a.fromBox ?? a.from)).map(a => a.fromBox ?? a.from),
      resolvePredecessor: (pid) => {
        const pred = taskById2.get(pid);
        if (!pred) return null;
        if (pred.type === 'startday') return endOffsetToDate(parseInt((pred as any).text ?? '0') || 0, true);
        if (pred.alwaysOn || pred.is24_5) {
          const predClockStart = walk(pid);
          return pred.is24_5
            ? addWeekdayHours(predClockStart, pred.duration, ganttNonWorkingDays, ganttWorkDays)
            : new Date(predClockStart.getTime() + pred.duration * 3600000);
        }
        // A regular predecessor's own schedule column always decodes correctly through
        // the standard work-hour grid — same decode getEnd/getEndDate use for a regular
        // task's own end. Previously an is24h box's regular (non-is24h) predecessors
        // were silently ignored here entirely — only is24h predecessors contributed to
        // the max — relying on t.start already reflecting the regular contribution via
        // calcStart's own max-over-predecessors. That's only true when nothing here
        // needed a real-time (not schedule-column) decode, i.e. when there was no is24h
        // predecessor in the mix at all; the fallback branch below already covers that
        // case identically. Resolving every predecessor uniformly here (mirroring
        // computeVisualPositions' own resolvePredecessor) correctly takes the real max
        // for a box with BOTH a regular and an is24h predecessor, instead of only ever
        // considering the is24h one.
        return endOffsetToDate(pred.start + pred.duration, pred.duration === 0);
      },
      // earliestStart is a REAL calendar floor for an is24h box — it can fall on a
      // non-work day (the whole point of running 24/7) — so it must be compared
      // directly as a Date, never via a schedule column: calcStart's
      // calcEarliestStartOffset encodes it into the work-day-only schedule-column axis
      // shared with regular tasks, which cannot represent a weekend/non-work date at all
      // and silently snaps it to the nearest work-day-equivalent column. Mirrors
      // computeVisualPositions' own applyEarliestStart callback, with full time-of-day
      // support to match calcEarliestStartOffset's own hours-mode parsing, instead of
      // always flooring to the configured day-start hour.
      applyEarliestStart: (x, result) => {
        const b = boxes.find(bb => bb.id === x);
        if (!b?.earliestStart || b.earliestStart.startsWith('W')) return result;
        const [datePart, timePart] = b.earliestStart.split('T');
        const [ey, em, ed] = datePart.split('-').map(Number);
        const esDt = timePart
          ? (() => { const [h, mi] = timePart.split(':').map(Number); return new Date(ey, em - 1, ed, h || 0, mi || 0, 0); })()
          : new Date(ey, em - 1, ed, workDayStartHour, 0, 0);
        return esDt > result ? esDt : result;
      },
    });

    return walk(id);
  };

  // Real clock start of every alwaysOn/is24_5 task, for GanttPanel's own bar
  // positioning — passed down so the Gantt view's rendered positions for is24h
  // chains match what getAlwaysOnClockStart already computes correctly here for
  // the boxes' own displayed start/end labels.
  const is24hRealStarts = useMemo(() => {
    const map = new Map<number, Date>();
    ganttTasks.forEach(t => {
      if (t.type === 'callout' || t.type === 'startday') return;
      if (t.alwaysOn || t.is24_5) map.set(t.id, getAlwaysOnClockStart(t.id, ganttTasks));
    });
    // A subitem only ever becomes is24h via inheritance from its parent (see
    // lib/gantt.ts's calcSubitemGanttTasks) — so whenever one is, its parent is
    // guaranteed to already have an entry above. Walk subitems against the combined
    // top-level+subitem task list (so a subitem predecessor resolves correctly, same
    // resolvePredecessor logic as any other box), anchoring each one's root-of-chain
    // fallback to its own parent's real start instead of the generic project fallback.
    const allWithSubitems = [...ganttTasks, ...subitemGanttTasks];
    subitemGanttTasks.forEach(t => {
      if (!(t.alwaysOn || t.is24_5)) return;
      const parentFallback = t.parentBoxId !== undefined ? map.get(t.parentBoxId) : undefined;
      map.set(t.id, getAlwaysOnClockStart(t.id, allWithSubitems, parentFallback));
    });
    return map;
  }, [ganttTasks, subitemGanttTasks, schedulingFingerprint, arrows, ganttNonWorkingDays, ganttWorkDays, ganttStartDate, workHoursOnly, durationUnit, fullDaySchedule, workDayStartHour, workDayHoursPerDay]); // eslint-disable-line react-hooks/exhaustive-deps

  const getEnd = (id: number): string => {
    const t = ganttTaskById.get(id);
    if (!t) return '0';
    const end = t.start + t.duration;

    const hpdEnd = fullDaySchedule ? 24 : workDayHoursPerDay;
    if (endTimeDisplay.mode === 'offset') {
      if (durationUnit === 'hours') return `${Math.round(end * 2) / 2}h`;
      if (durationUnit === 'weeks') return `${Math.round(end * 10) / 10}w`;
      // sub-day in days mode: show as hours rather than "0d"
      if (end < 1) return `${Math.round(end * hpdEnd * 2) / 2}h`;
      return `${Math.round(end)}d`;
    }

    // For days/weeks mode with a sub-day task: derive actual clock time from raw duration.
    // Skipped for a box with rolled-up subitems — its raw duration string is stale
    // (applySubitemRollup only sets rolledUpDuration, never rewrites the display
    // string), so re-parsing it here would show the wrong clock time; falls through
    // to the general end-time computation below instead, which already uses t.duration.
    const box = boxById.get(id);
    if ((durationUnit === 'days' || durationUnit === 'weeks') && outerBoxesById.get(id)?.rolledUpDuration === undefined) {
      const dm = box?.duration?.match(/^(\d+(?:\.\d+)?)\s*([hdwHDW]?)$/);
      if (dm) {
        const dn = parseFloat(dm[1]);
        const du = (dm[2] || 'd').toLowerCase();
        const workHours = du === 'h' ? dn : du === 'd' ? dn * workDayHoursPerDay : dn * workDayHoursPerDay * 5;
        if (workHours < hpdEnd) {
          // endOffsetToDate(t.start) gives the correct calendar date; override the time
          const dt2 = endOffsetToDate(t.start, true);  // position semantics: start-date of this task
          const defaultDayStart = fullDaySchedule ? 0 : workDayStartHour;
          const endBox = boxById.get(id);
          const dayStart = (endBox?.scheduledStart?.includes('T'))
            ? (() => { const [h, m] = endBox.scheduledStart!.split('T')[1].split(':').map(Number); return (h || 0) + (m || 0) / 60; })()
            : defaultDayStart;
          dt2.setHours(Math.floor(dayStart + workHours), Math.round(((dayStart + workHours) % 1) * 60), 0, 0);
          return formatDisplayDate(dt2, endTimeDisplay);
        }
      }
    }

    // alwaysOn / is24_5 in hours mode: end = start-clock-time + clock-hours
    if ((t.alwaysOn || t.is24_5) && durationUnit === 'hours' && t.duration >= 0) {
      // Reuse the memoized is24hRealStarts map instead of re-walking the whole
      // predecessor chain live — this runs per box, per render, for every
      // alwaysOn/is24_5 box shown on the canvas (see getStart/getStartDate/
      // getEndDate below for the same fix). Fallback only covers a task type
      // is24hRealStarts deliberately skips (callout/startday); should never be
      // reached for an alwaysOn/is24_5 box in practice.
      const startDt = is24hRealStarts.get(id) ?? getAlwaysOnClockStart(id, ganttTasks);
      const endDt = t.is24_5
        ? addWeekdayHours(startDt, t.duration, ganttNonWorkingDays, ganttWorkDays)
        : new Date(startDt.getTime() + t.duration * 3600000);
      return formatDisplayDate(endDt, endTimeDisplay);
    }

    const dt = endOffsetToDate(end, t.duration === 0);
    return formatDisplayDate(dt, endTimeDisplay);
  };

  const getStart = (id: number): string => {
    const t = ganttTaskById.get(id);
    if (!t) return '';
    // First block (offset 0): always show project start date in calendar format
    if (t.start === 0) {
      return formatDisplayDate(new Date(ganttStartDate), endTimeDisplay);
    }
    // Offset mode for subsequent blocks
    if (endTimeDisplay.mode === 'offset') {
      if (durationUnit === 'hours') return `${Math.round(t.start * 2) / 2}h`;
      if (durationUnit === 'weeks') return `${Math.round(t.start * 10) / 10}w`;
      return `${Math.round(t.start)}d`;
    }
    if ((t.alwaysOn || t.is24_5) && durationUnit === 'hours') {
      const startDt = is24hRealStarts.get(id) ?? getAlwaysOnClockStart(id, ganttTasks);
      return formatDisplayDate(startDt, endTimeDisplay);
    }
    const dt = endOffsetToDate(t.start, true);
    // If scheduledStart has a time component, use it directly regardless of duration mode
    const startBox = boxById.get(id);
    if (endTimeDisplay.time && startBox?.scheduledStart?.includes('T')) {
      const [sh, sm] = startBox.scheduledStart.split('T')[1].split(':').map(Number);
      dt.setHours(sh || 0, sm || 0, 0, 0);
    }
    return formatDisplayDate(dt, endTimeDisplay);
  };

  // Precomputed once (not exposed as a function prop) so it can also be handed to
  // lib/svgExport.ts's pure export function, same pattern as is24hRealStarts above —
  // for the Gantt's "Show start/end column" view option. Top-level tasks only:
  // getStart/getEnd aren't valid for subitem ids (ganttTaskById, which they're both
  // keyed off, comes from outerBoxes — subitems are excluded from it by the existing
  // rollup architecture), so a subitem id simply has no entry here.
  const ganttStartEndText = useMemo(() => {
    const map = new Map<number, { start: string; end: string }>();
    ganttTasks.forEach(t => {
      if (t.type === 'callout' || t.type === 'startday') return;
      map.set(t.id, { start: getStart(t.id), end: getEnd(t.id) });
    });
    return map;
  }, [ganttTasks, schedulingFingerprint, endTimeDisplay, arrows, ganttNonWorkingDays, ganttWorkDays, ganttStartDate, workHoursOnly, durationUnit, fullDaySchedule, workDayStartHour, workDayHoursPerDay]); // eslint-disable-line react-hooks/exhaustive-deps

  // Real calendar Start/Finish dates for external export (XLSX). Deliberately separate from
  // getStart/getEnd above: those return display STRINGS that skip the year and can be in
  // "offset" mode (e.g. "5d") depending on the user's endTimeDisplay setting — neither is
  // usable for a file another tool needs to import as real dates, and this mirrors only the
  // "calendar" branches of getStart/getEnd rather than risk touching that already-tuned logic.
  const getStartDate = (id: number): Date => {
    const t = ganttAndSubitemTaskById.get(id);
    if (!t || t.start === 0) return new Date(ganttStartDate);
    if ((t.alwaysOn || t.is24_5) && durationUnit === 'hours') {
      return is24hRealStarts.get(id) ?? getAlwaysOnClockStart(id, ganttTasks);
    }
    const dt = endOffsetToDate(t.start, true);
    const startBox = boxes.find(b => b.id === id);
    if (startBox?.scheduledStart?.includes('T')) {
      const [sh, sm] = startBox.scheduledStart.split('T')[1].split(':').map(Number);
      dt.setHours(sh || 0, sm || 0, 0, 0);
    }
    return dt;
  };

  const getEndDate = (id: number): Date => {
    const t = ganttAndSubitemTaskById.get(id);
    if (!t) return new Date(ganttStartDate);
    const end = t.start + t.duration;
    const hpdEnd = fullDaySchedule ? 24 : workDayHoursPerDay;

    // See getEnd's identical guard above: skip the stale raw-duration reparse for a
    // box with rolled-up subitems.
    const box = boxes.find(b => b.id === id);
    if ((durationUnit === 'days' || durationUnit === 'weeks') && outerBoxesById.get(id)?.rolledUpDuration === undefined) {
      const dm = box?.duration?.match(/^(\d+(?:\.\d+)?)\s*([hdwHDW]?)$/);
      if (dm) {
        const dn = parseFloat(dm[1]);
        const du = (dm[2] || 'd').toLowerCase();
        const workHours = du === 'h' ? dn : du === 'd' ? dn * workDayHoursPerDay : dn * workDayHoursPerDay * 5;
        if (workHours < hpdEnd) {
          const dt2 = endOffsetToDate(t.start, true);
          const defaultDayStart = fullDaySchedule ? 0 : workDayStartHour;
          const dayStart = (box?.scheduledStart?.includes('T'))
            ? (() => { const [h, m] = box!.scheduledStart!.split('T')[1].split(':').map(Number); return (h || 0) + (m || 0) / 60; })()
            : defaultDayStart;
          dt2.setHours(Math.floor(dayStart + workHours), Math.round(((dayStart + workHours) % 1) * 60), 0, 0);
          return dt2;
        }
      }
    }

    if ((t.alwaysOn || t.is24_5) && durationUnit === 'hours' && t.duration >= 0) {
      const startDt = is24hRealStarts.get(id) ?? getAlwaysOnClockStart(id, ganttTasks);
      return t.is24_5
        ? addWeekdayHours(startDt, t.duration, ganttNonWorkingDays, ganttWorkDays)
        : new Date(startDt.getTime() + t.duration * 3600000);
    }

    return endOffsetToDate(end, t.duration === 0);
  };

  return {
    ganttTasks, subitemGanttTasks, criticalPath, schedulingConflicts,
    calcEarliestStartOffset, endOffsetToDate,
    getStart, getEnd, getStartDate, getEndDate, ganttStartEndText,
    is24hRealStarts, renderedArrows, arrowBoxes, arrowBoxesById,
  };
}
